import { Badge } from '@/modules/Badge';
import { Popup } from '@/modules/Popup';
import { Tooltip } from '@/modules/Tooltip';
import { cn } from '@/shared/utils/cn';
import type { BadgeColor } from '@/shared/types/badge';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import type { NormalizedCatalogCardTag } from '../../utils/catalogCardTagUtils';
import { menuPanelBox, stopCardClick } from './catalogCardMenuPanelUtils';
import type { ReactElement } from 'react';

const GAP_CLASS = 'ui-gap-8';

type CatalogTagTooltipWrap = 'default' | 'fillRow';

/**
 * - `default`: chip en fila múltiple (no se encoge; el layout mide y usa `+n`).
 * - `fillRow`: una sola tag; ancho intrínseco (`w-fit`) con tope `max-w-full`. Si el label es
 *   corto, el indicador no se estira; si es largo, toca el tope, ellipsis y tooltip.
 */
function wrapTagBadgeWithTooltip(
  badge: ReactElement,
  tag: NormalizedCatalogCardTag,
  wrap: CatalogTagTooltipWrap = 'default'
) {
  const tip = tag.tooltip?.trim();
  const fillRowBox = 'ui-inline-flex ui-w-fit ui-min-w-0 ui-max-w-full' as const;
  if (!tip) {
    if (wrap === 'fillRow') {
      return <span className={fillRowBox}>{badge}</span>;
    }
    return badge;
  }
  const triggerClass = wrap === 'fillRow' ? fillRowBox : 'ui-inline-flex ui-max-w-none ui-shrink-0';
  return (
    <Tooltip label={tip} position="top" preserveLayout {...tag.tooltipProps}>
      <span className={triggerClass}>{badge}</span>
    </Tooltip>
  );
}

const badgeHoverInCardRow = false;
const badgeHoverInPopup = true;

const badgeBaseSoftMd = {
  theme: 'soft' as const,
  size: 'md' as const,
  truncateLabel: false as const,
};

/** Mismo criterio que `stroke` temático del {@link Badge}; solo con `group-hover` de la tarjeta. */
const GROUP_HOVER_BORDER: Record<BadgeColor, string> = {
  accent: 'group-hover:!ui-border-brand',
  gray: 'group-hover:!ui-border',
  success: 'group-hover:!ui-border-success',
  warning: 'group-hover:!ui-border-warning',
  danger: 'group-hover:!ui-border-danger',
  info: 'group-hover:!ui-border-info',
};

/**
 * En fila: sin `stroke` del Badge por defecto; borde semántico al hover del padre `group`.
 * Con `emphasized`, el Badge usa `stroke` nativo (borde siempre visible).
 */
function catalogTagCardBorderClass(color: BadgeColor, emphasized?: boolean) {
  if (emphasized) return '';
  return cn(
    'ui-border-1 ui-border-transparent ui-transition-colors ui-duration-200 ui-ease-in-out',
    GROUP_HOVER_BORDER[color]
  );
}

type CatalogCatalogCardTagsRowProps = {
  /** Etiquetas ya normalizadas (texto + color del {@link Badge}). */
  tags: NormalizedCatalogCardTag[];
  /** Si es `true`, el trazo del badge queda fijo (comportamiento `stroke` del tema); si no, solo al hover del grupo. */
  emphasized?: boolean;
  /** Con `true`, no se abre el popup de `+n` (solo lectura). */
  disabled?: boolean;
  className?: string;
};

function badgePropsCardInRow(emphasized: boolean | undefined) {
  return (color: NormalizedCatalogCardTag['color']) => ({
    ...badgeBaseSoftMd,
    stroke: Boolean(emphasized),
    color,
  });
}

/**
 * Single tag chip for compact catalog headers (e.g. `layout="stacked"` top row).
 * Omits the vertical padding used by {@link CatalogCatalogCardTagsRow} in the body/footer.
 */
export function CatalogCatalogCardStackedHeaderTag({
  tag,
  emphasized,
  disabled = false,
  className,
}: {
  tag: NormalizedCatalogCardTag;
  emphasized?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const badgePropsCard = badgePropsCardInRow(emphasized);
  return (
    <span
      className={cn('ui-inline-flex ui-min-w-0 ui-max-w-[min(100%,140px)] ui-shrink-0', className)}
    >
      {wrapTagBadgeWithTooltip(
        <Badge
          {...badgePropsCard(tag.color)}
          hover={!disabled}
          label={tag.label}
          truncateLabel
          truncateTooltip
          className={cn(
            'ui-w-fit ui-min-w-0 ui-max-w-full',
            catalogTagCardBorderClass(tag.color, emphasized)
          )}
        />,
        tag,
        'fillRow'
      )}
    </span>
  );
}

/** Un solo badge: tamaño al texto; si desborda la fila, `…` + tooltip (no chip `+1`). */
function CatalogCardTagsRowOneTag({
  tag,
  emphasized,
  className,
}: {
  tag: NormalizedCatalogCardTag;
  emphasized?: boolean;
  className?: string;
}) {
  const badgePropsCard = badgePropsCardInRow(emphasized);
  return (
    <div className={cn('ui-relative ui-w-full ui-min-w-0 ui-pt-8', className)}>
      <div className="ui-flex ui-w-full ui-min-w-0">
        {wrapTagBadgeWithTooltip(
          <Badge
            {...badgePropsCard(tag.color)}
            hover
            label={tag.label}
            truncateLabel
            truncateTooltip
            className={cn(
              'ui-w-fit ui-min-w-0 ui-max-w-full',
              catalogTagCardBorderClass(tag.color, emphasized)
            )}
          />,
          tag,
          'fillRow'
        )}
      </div>
    </div>
  );
}

/**
 * Fila múltiple (≥2 tags): desborde a `+n` + `Popup` (lógica de medida).
 */
function CatalogCardTagsRowMulti({
  tags,
  emphasized,
  disabled = false,
  className,
}: CatalogCatalogCardTagsRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState(() => tags.length);

  const n = tags.length;

  const badgePropsCard = badgePropsCardInRow(emphasized);

  const badgePropsPopup = (color: NormalizedCatalogCardTag['color']) => ({
    ...badgeBaseSoftMd,
    stroke: true,
    color,
  });

  useLayoutEffect(() => {
    if (n === 0) {
      setVisibleCount(0);
      return;
    }

    setVisibleCount(n);

    const compute = () => {
      const container = containerRef.current;
      const measureRoot = measureRef.current;
      if (!container || !measureRoot) return;

      const available = container.clientWidth;
      if (available <= 0) return;

      const gapPx = measureGapPx(measureRoot);

      const tagWrappers = measureRoot.querySelectorAll<HTMLElement>('[data-catalog-tag-measure]');
      const tagWidths: number[] = [];
      tagWrappers.forEach((el) => tagWidths.push(el.offsetWidth));

      const overflowWidths: number[] = [];
      for (let hidden = 1; hidden <= n; hidden++) {
        const el = measureRoot.querySelector<HTMLElement>(`[data-hidden-count="${hidden}"]`);
        overflowWidths[hidden] = el?.offsetWidth ?? 0;
      }

      let bestK = 0;
      for (let k = n; k >= 0; k--) {
        const hidden = n - k;
        let total = 0;
        for (let i = 0; i < k; i++) {
          total += tagWidths[i] ?? 0;
        }
        if (hidden > 0) {
          total += overflowWidths[hidden] ?? 0;
        }
        const itemCount = k + (hidden > 0 ? 1 : 0);
        if (itemCount > 1) {
          total += gapPx * (itemCount - 1);
        }

        if (total <= available) {
          bestK = k;
          break;
        }
      }

      setVisibleCount(bestK);
    };

    compute();

    const el = containerRef.current;
    const mr = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    if (mr) ro.observe(mr);
    return () => ro.disconnect();
  }, [tags, n]);

  if (n === 0) {
    return null;
  }

  const hidden = n - visibleCount;
  const overflowTags = hidden > 0 ? tags.slice(visibleCount) : [];
  return (
    <div className={cn('ui-relative ui-w-full ui-min-w-0 ui-pt-8', className)}>
      <div
        ref={measureRef}
        className={cn(
          'ui-pointer-events-none ui-fixed ui-left-[-9999px] ui-top-0 ui-z-[-1] ui-flex ui-flex-nowrap',
          GAP_CLASS
        )}
        aria-hidden
      >
        {tags.map((tag, i) => (
          <span
            key={`m-${i}`}
            data-catalog-tag-measure
            className="ui-inline-flex ui-max-w-none ui-shrink-0"
          >
            {wrapTagBadgeWithTooltip(
              <Badge
                {...badgePropsCard(tag.color)}
                hover={badgeHoverInCardRow}
                label={tag.label}
                className={cn(
                  'ui-whitespace-nowrap',
                  catalogTagCardBorderClass(tag.color, emphasized)
                )}
              />,
              tag
            )}
          </span>
        ))}
        {Array.from({ length: n }, (_, idx) => {
          const h = idx + 1;
          return (
            <span
              key={`mo-${h}`}
              data-catalog-overflow-measure
              data-hidden-count={h}
              className="ui-inline-flex ui-max-w-none ui-shrink-0"
            >
              <Badge
                {...badgePropsCard('accent')}
                hover={badgeHoverInCardRow}
                label={`+${h}`}
                className={cn(
                  'ui-whitespace-nowrap',
                  catalogTagCardBorderClass('accent', emphasized)
                )}
              />
            </span>
          );
        })}
      </div>

      <div
        ref={containerRef}
        className={cn(
          'ui-flex ui-w-full ui-min-w-0 ui-flex-nowrap ui-items-center ui-overflow-hidden ui-whitespace-nowrap',
          GAP_CLASS
        )}
      >
        {tags.slice(0, visibleCount).map((tag, i) => (
          <Fragment key={`${tag.label}-${i}`}>
            {wrapTagBadgeWithTooltip(
              <Badge
                {...badgePropsCard(tag.color)}
                hover={badgeHoverInCardRow}
                label={tag.label}
                className={cn(
                  'ui-max-w-none ui-shrink-0 ui-whitespace-nowrap',
                  catalogTagCardBorderClass(tag.color, emphasized)
                )}
              />,
              tag
            )}
          </Fragment>
        ))}
        {hidden > 0 ? (
          disabled ? (
            <span className="ui-inline-flex ui-shrink-0">
              <Badge
                {...badgePropsCard('accent')}
                hover={badgeHoverInCardRow}
                label={`+${hidden}`}
                className={cn(
                  'ui-max-w-none ui-shrink-0 ui-whitespace-nowrap',
                  catalogTagCardBorderClass('accent', emphasized)
                )}
              />
            </span>
          ) : (
            <>
              <button
                ref={overflowTriggerRef}
                type="button"
                className="ui-pointer-events-auto ui-inline-flex ui-shrink-0 ui-cursor-pointer ui-border-0 ui-bg-transparent ui-p-0"
                aria-label={`Ver ${hidden} etiquetas más: ${overflowTags.map((t) => t.label).join(', ')}`}
                aria-haspopup="dialog"
                onClick={stopCardClick}
              >
                <Badge
                  {...badgePropsCard('accent')}
                  hover={badgeHoverInCardRow}
                  label={`+${hidden}`}
                  className={cn(
                    'ui-max-w-none ui-shrink-0 ui-whitespace-nowrap',
                    catalogTagCardBorderClass('accent', emphasized)
                  )}
                />
              </button>
              {/* Anfitrión sin anchura para no romper flex-nowrap; el panel va en portal */}
              <div className="ui-inline-block ui-h-0 ui-w-0 ui-shrink-0 ui-overflow-visible">
                <Popup
                  triggerRef={overflowTriggerRef}
                  position="bottom-left"
                  color="secondary"
                  size="xs"
                  theme="solid"
                  rounded
                  pill="xs"
                  popoverArrow={false}
                  stopPropagationList
                >
                  <div
                    className={cn(
                      menuPanelBox,
                      'ui-flex ui-max-h-60 ui-min-w-44 ui-flex-col ui-gap-8 ui-overflow-y-auto ui-p-12'
                    )}
                    role="list"
                  >
                    {overflowTags.map((tag, i) => (
                      <div
                        key={`overflow-${tag.label}-${i}`}
                        role="listitem"
                        className="ui-w-fit ui-self-start"
                      >
                        {wrapTagBadgeWithTooltip(
                          <Badge
                            {...badgePropsPopup(tag.color)}
                            hover={badgeHoverInPopup}
                            label={tag.label}
                            className="ui-whitespace-nowrap"
                          />,
                          tag
                        )}
                      </div>
                    ))}
                  </div>
                </Popup>
              </div>
            </>
          )
        ) : null}
      </div>
    </div>
  );
}

/**
 * Fila única de badges: ajusta al ancho del contenedor y muestra `+n` por los que no caben.
 * El `+n` abre un `Popup` con el resto de etiquetas como lista de badges.
 * Con **una sola** etiqueta: el chip mantiene ancho al texto; no se usa `+1`. Si el label
 * desborda el ancho de la fila, se trunca con `…` y el detalle al pasar el ratón
 * (mismo criterio que `Badge` con `truncateLabel` + `truncateTooltip`).
 */
export function CatalogCatalogCardTagsRow({
  tags,
  emphasized,
  disabled = false,
  className,
}: CatalogCatalogCardTagsRowProps) {
  if (tags.length === 0) {
    return null;
  }
  if (tags.length === 1) {
    return (
      <CatalogCardTagsRowOneTag tag={tags[0]!} emphasized={emphasized} className={className} />
    );
  }
  return (
    <CatalogCardTagsRowMulti
      tags={tags}
      emphasized={emphasized}
      disabled={disabled}
      className={className}
    />
  );
}

function measureGapPx(container: HTMLElement): number {
  if (typeof window === 'undefined') return 8;
  const g = window.getComputedStyle(container).columnGap || window.getComputedStyle(container).gap;
  const parsed = parseFloat(g);
  return Number.isFinite(parsed) ? parsed : 8;
}
